import { useCallback, useMemo } from "react";

import { useSnackbar } from "notistack";
import { UseFormReturn } from "react-hook-form";

import { EmailContentBuilderType } from "../../EmailEditor/useGetEmailContent";
import { EmailContentFormData } from "../types";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";

interface UseSavePreferencesProps {
  emailContent?: EmailContentBuilderType | null;
  form: UseFormReturn<EmailContentFormData>;
  setIsLoading: (loading: boolean) => void;
  refetchEmailContent: () => Promise<void>;
  onEmailContentDeleted: () => void;
}

export const useSavePreferences = ({
  emailContent,
  form,
  setIsLoading,
  refetchEmailContent,
  onEmailContentDeleted,
}: UseSavePreferencesProps) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync: updateEmailPreferencesMutation } = trpc.updateEmailPreferences.useMutation();

  const onSubmit = useCallback(
    async (data: EmailContentFormData) => {
      if (!emailContent?.id) {
        return;
      }

      setIsLoading(true);

      const { error } = await updateEmailPreferencesMutation({
        emailContentId: emailContent.id,
        subject: data.subject,
        previewText: data.previewText,
        senderName: data.senderName,
        replyToUserId: data.replyToUserId,
        attachmentUrl: data.attachment?.fileUrl || null,
      });

      if (error) {
        setIsLoading(false);
        if (error.code === "email_content_not_found") {
          onEmailContentDeleted();
          return;
        }
        enqueueSnackbar({ vibe: "danger", message: t("dashboard", "email.save.error") });
        throw new Error(`updateEmailPreferences failed: ${error.code}`);
      }

      await refetchEmailContent();
      setIsLoading(false);
    },
    [
      emailContent?.id,
      setIsLoading,
      updateEmailPreferencesMutation,
      enqueueSnackbar,
      t,
      refetchEmailContent,
      onEmailContentDeleted,
    ]
  );

  const save = useMemo(() => form.handleSubmit(onSubmit), [form, onSubmit]);

  return { save };
};
